import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { ChevronsRight, CircleAlert, Eye, MapPin } from "lucide-react"
import { cn } from "@/lib/utils"
import { ACTIONS, RouteStop } from "@/reducers/routes2gSlice"
import { HoverPopover } from "@/components/ui/hover-popover"
import { isMobile } from "@/utils/detectDevice"
import React from "react"
import { Button } from "../ui/button"
import { Badge } from "@/components/ui/badge"
import StationaryIndicator from "@/components/StationaryIndicator"
import {
  MessageBubbleIcon,
  MessageBubblePlusIcon,
} from "@/components/icons/MessageBubbleIcon"
import { useTranslation } from "react-i18next"

export interface DriverCardProps {
  name: string
  avatarName: string | undefined
  avatarPhoto?: string | undefined
  avatarColor: string

  plates?: string | undefined

  hasRoute?: boolean
  nextStop?: RouteStop
  stationarySince?: string

  distance?: string
  popoverContent?: React.ReactNode

  isDriving: boolean
  isLate: boolean
  isNextStopLate: boolean
  isTracked: boolean
  isOffRoute: boolean
  showRouteStatusBadge?: boolean
  showAlert: boolean

  unreadCount?: number

  isActive: boolean
  isHovered: boolean

  onSelectDriver: () => void
  onFlyToDriver: () => void
  onNavigateToChat: (jid: string, shouldJoin?: boolean) => void
  chatJid?: string | undefined
  shouldJoin?: boolean
  /** Who dispatches this vehicle; shown to a fleet manager only. */
  dispatcherLabel?: string
}

const getActionIcon = (stop: RouteStop) => {
  const action =
    ACTIONS.find((action) => action.value.toLowerCase() === stop.action) ||
    ACTIONS.find((action) => action.value === "other")!

  return <action.icon className="h-3 w-3" size={12} />
}

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2)
}

function DriverCard({
  name,
  avatarName,
  avatarPhoto,
  avatarColor,
  plates,
  distance,
  popoverContent,
  unreadCount = 0,
  isDriving,
  isLate,
  isNextStopLate,
  hasRoute,
  isTracked,
  isOffRoute,
  showRouteStatusBadge,
  showAlert,
  nextStop,
  stationarySince,
  isActive,
  isHovered,
  chatJid,
  onSelectDriver,
  onFlyToDriver,
  onNavigateToChat,
  shouldJoin,
  dispatcherLabel,
}: DriverCardProps) {
  const { t } = useTranslation()
  const shouldShowRouteStatusBadge = showRouteStatusBadge ?? isTracked

  return (
    <HoverPopover
      disabled={isMobile || !popoverContent}
      content={popoverContent}
      side="right"
    >
      <div
        className={cn(
          "flex max-h-[84px] min-h-[84px] min-w-[360px] cursor-pointer items-center gap-2 py-3 pl-4",
          isActive
            ? "bg-sidebar-accent hover:rounded-md"
            : "hover:rounded-md hover:bg-sidebar-accent",
        )}
      >
        {/* Avatar */}
        <div className="relative self-start">
          <Avatar className={cn(isDriving && "border-2 border-green-600")}>
            {avatarPhoto && <AvatarImage src={avatarPhoto} alt={name} />}
            <AvatarFallback
              style={{ backgroundColor: avatarColor }}
              className="font-medium text-gray-900"
            >
              {getInitials(avatarName ?? name)}
            </AvatarFallback>
          </Avatar>
          {isDriving && (
            <div className="absolute -bottom-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-green-600">
              <ChevronsRight
                className="text-white dark:text-primary-foreground"
                size={14}
              />
            </div>
          )}
        </div>

        {/* Content */}
        <div className="text-md mb-0.5 flex min-w-0 flex-1 flex-col self-start">
          {/* Header */}
          <div className="flex items-center justify-between">
            <h3 className="truncate font-semibold text-gray-900 dark:text-gray-100">
              {name}
            </h3>
          </div>

          {/* Plates, and for a fleet manager who dispatches the vehicle */}
          <div className="mb-0.5 flex items-center justify-between">
            <p className="truncate pr-1 text-muted-foreground">
              {plates}
              {dispatcherLabel && (
                <span
                  className="ml-1 text-xs"
                  data-testid="driver-card-dispatcher"
                >
                  · {dispatcherLabel}
                </span>
              )}
            </p>
          </div>

          {/* Status and Distance */}
          {((hasRoute && nextStop) || shouldShowRouteStatusBadge) && (
            <div className="flex items-center gap-2">
              {distance && nextStop && (
                <Badge
                  variant={
                    showAlert
                      ? isNextStopLate
                        ? "error"
                        : "positive"
                      : "neutral"
                  }
                >
                  {getActionIcon(nextStop)}
                  {distance}
                </Badge>
              )}
              {distance && nextStop && (
                <StationaryIndicator
                  proximityStatus={nextStop.proximity_status}
                  stationarySince={stationarySince}
                />
              )}
              {hasRoute && nextStop && isLate && !isNextStopLate && (
                <Badge
                  variant={"error"}
                  title={t("fleet.fleetOverview.lateTitle")}
                >
                  <CircleAlert className="h-3 w-3" />
                  {t("fleet.fleetOverview.late")}
                </Badge>
              )}
              {shouldShowRouteStatusBadge && (
                <Badge variant={isOffRoute ? "error" : "positive"}>
                  {isOffRoute ? (
                    <>
                      <CircleAlert className="h-3 w-3" />
                      {t("fleet.fleetOverview.offRoute")}
                    </>
                  ) : (
                    <>
                      <Eye className="h-3 w-3" />
                      {t("fleet.fleetOverview.onRoute")}
                    </>
                  )}
                </Badge>
              )}
            </div>
          )}
        </div>
        {/* Hover Actions */}
        <div
          className={`mr-2 flex items-center gap-2 transition-all duration-200 ${
            isHovered
              ? "translate-x-0 opacity-100"
              : "pointer-events-none translate-x-4 opacity-0"
          }`}
        >
          <Button
            variant="outline"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-sidebar-accent"
            onClick={() => {
              onSelectDriver()
              onFlyToDriver()
            }}
          >
            <MapPin className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="relative h-8 w-8 p-0 hover:bg-sidebar-accent"
            onClick={
              chatJid ? () => onNavigateToChat(chatJid, shouldJoin) : undefined
            }
            disabled={!chatJid}
          >
            {shouldJoin && <MessageBubblePlusIcon className="h-4 w-4" />}
            {!shouldJoin && <MessageBubbleIcon className="h-4 w-4" />}
            {!shouldJoin && unreadCount > 0 && isHovered && (
              <div className="absolute -right-1/4 -top-1/4 flex h-5 min-w-5 items-center justify-center rounded-lg bg-base-primary p-1 font-semibold text-white">
                {unreadCount}
              </div>
            )}
          </Button>
        </div>
      </div>
    </HoverPopover>
  )
}

export { DriverCard }
